import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import settingsIcon from '../../../static/icons/PurpleSettingIcon.svg';
import MultiSelectDropdown from '../MultiSelectDropdown';
import useSettingsFilters from '../../../hooks/useSettingsFilters';
import { formatYearDisplay } from '../../../utils/strings';
import Toast from '../../Toast';

const getSubjectIdentifier = (s) =>
  (typeof s === 'object' && s !== null ? s.code || s.subjectCode || s.value || s : s);

const ItemSettingsModal = ({
  isOpen,
  open,
  onClose,
  onSave,
  item,
  state,
  availableYears: propAvailableYears,
  availableSubjects: propAvailableSubjects,
  taskTypes: propTaskTypes,
  responseTypes: propResponseTypes,
  selectedYears: propSelectedYears,
  setSelectedYears: propSetSelectedYears,
  selectedSubjects: propSelectedSubjects,
  setSelectedSubjects: propSetSelectedSubjects,
  selectedTaskTypes: propSelectedTaskTypes,
  setSelectedTaskTypes: propSetSelectedTaskTypes,
  selectedResponseTypes: propSelectedResponseTypes,
  setSelectedResponseTypes: propSetSelectedResponseTypes,
}) => {
  const isControlled = propSetSelectedYears != null;
  const shouldOpen = isOpen ?? open;

  const [draft, setDraft] = useState({
    years: [],
    subjects: [],
    taskTypes: [],
    responseTypes: [],
  });
  const [draftSynced, setDraftSynced] = useState(false);

  useEffect(() => {
    if (!shouldOpen) {
      setDraftSynced(false);
      return;
    }
    if (isControlled && !draftSynced) {
      setDraft({
        years: propSelectedYears ?? [],
        subjects: propSelectedSubjects ?? [],
        taskTypes: propSelectedTaskTypes ?? [],
        responseTypes: propSelectedResponseTypes ?? [],
      });
      setDraftSynced(true);
    }
  }, [shouldOpen, isControlled, draftSynced, propSelectedYears, propSelectedSubjects, propSelectedTaskTypes, propSelectedResponseTypes]);

  const initialValues = useMemo(() => {
    if (!item) return { years: [], subjects: [], taskTypes: [], responseTypes: [] };
    const meta = item.metadata || {};
    return {
      years: meta.years?.length ? meta.years : (item.years || (item.year ? [item.year] : [])),
      subjects: (meta.subjects?.length ? meta.subjects : (item.subjects || [])).map(getSubjectIdentifier).filter(Boolean),
      taskTypes: meta.taskTypes?.length ? meta.taskTypes : (item.taskTypes || (item.taskType ? [item.taskType] : [])),
      responseTypes: meta.responseTypes?.length ? meta.responseTypes : (item.responseTypes || (item.responseType ? [item.responseType] : [])),
    };
  }, [item]);

  const {
    availableYears: hookYears,
    availableSubjects: hookSubjects,
    availableTaskTypes: hookTaskTypes,
    availableResponseTypes: hookResponseTypes,
    selectedYears: managedYears,
    selectedSubjects: managedSubjects,
    selectedTaskTypes: managedTaskTypes,
    selectedResponseTypes: managedResponseTypes,
    taskTypeLabels: hookTaskTypeLabels,
    responseTypeLabels: hookResponseTypeLabels,
    setInternalResponseTypes,
    createYearSetter,
    createSubjectSetter,
    createTaskTypeSetter,
  } = useSettingsFilters({
    state,
    initialYears: initialValues.years,
    initialSubjects: initialValues.subjects,
    initialTaskTypes: initialValues.taskTypes,
    initialResponseTypes: initialValues.responseTypes,
    managedMode: !isControlled,
    item,
    isOpen: !!shouldOpen,
    enableYearFilter: false, // Don't filter subjects by year in detail view
  });

  const availableYears = propAvailableYears || hookYears || [];
  const availableSubjects = propAvailableSubjects || hookSubjects || [];
  const availableTaskTypes = propTaskTypes || hookTaskTypes || [];
  const availableResponseTypes = propResponseTypes || hookResponseTypes || [];

  const selectedYears = isControlled ? draft.years : managedYears;
  const setSelectedYears = isControlled
    ? (v) => setDraft((p) => ({ ...p, years: v, subjects: [], taskTypes: [], responseTypes: [] }))
    : createYearSetter();
  const selectedSubjects = isControlled ? draft.subjects : managedSubjects;
  const setSelectedSubjects = isControlled
    ? (v) => setDraft((p) => ({ ...p, subjects: v, taskTypes: [], responseTypes: [] }))
    : createSubjectSetter();
  const selectedTaskTypes = isControlled ? draft.taskTypes : managedTaskTypes;
  const setSelectedTaskTypes = isControlled
    ? (v) => setDraft((p) => ({ ...p, taskTypes: v, responseTypes: [] }))
    : createTaskTypeSetter();
  const selectedResponseTypes = isControlled ? draft.responseTypes : managedResponseTypes;
  const setSelectedResponseTypes = isControlled ? (v) => setDraft((p) => ({ ...p, responseTypes: v })) : setInternalResponseTypes;

  // Resolve a selected subject value to {subjectCode, subjectName} using availableSubjects
  const resolveSubject = (codeOrTitle) => {
    const searchValue = typeof codeOrTitle === 'object' && codeOrTitle !== null
      ? (codeOrTitle?.subjectCode || codeOrTitle?.code || codeOrTitle?.value)
      : codeOrTitle;
    const searchStr = String(searchValue || '');

    // Search availableSubjects, preferring real API subjects (code ≠ name) over fallback entries
    let found = null;
    let fallback = null;

    for (const s of availableSubjects) {
      const sCode = String(s.code || s.subjectCode || s.value || '');
      const sTitle = String(s.title || s.subjectName || s.name || '');
      const isCodeMatch = sCode === searchStr;
      const isNameMatch = sTitle.toLowerCase().trim() === searchStr.toLowerCase().trim();

      if (isCodeMatch || isNameMatch) {
        if (sCode && sTitle && sCode !== sTitle) {
          found = s;
          break;
        }
        if (!fallback) fallback = s;
      }
    }

    found = found || fallback;

    if (found) {
      return {
        subjectCode: found.subjectCode || found.code || found.value,
        subjectName: found.subjectName || found.title || found.name || found.label
      };
    }

    return null;
  };

  const handleSave = async () => {
    if (selectedYears.length === 0 || selectedSubjects.length === 0) {
      toast(<Toast message="Please select at least one Year and one Subject" />);
      return;
    }
    let settings;

    // Always resolve subjects to {subjectCode, subjectName} objects
    const subjectObjects = selectedSubjects.map(resolveSubject).filter(Boolean);

    if (isControlled) {
      settings = {
        years: selectedYears,
        subjects: subjectObjects,
        taskTypes: selectedTaskTypes,
        responseTypes: selectedResponseTypes,
      };
    } else {
      const itemTaskTypes = item?.taskTypes || (item?.taskType ? [item.taskType] : []);
      const itemResponseTypes = item?.responseTypes || (item?.responseType ? [item.responseType] : []);
      const taskTypesToUse = selectedTaskTypes.length > 0 ? selectedTaskTypes : itemTaskTypes;
      const responseTypesToUse = selectedResponseTypes.length > 0 ? selectedResponseTypes : itemResponseTypes;
      settings = {
        years: selectedYears,
        year: selectedYears[0] ?? null,
        subjects: subjectObjects,
        subject: subjectObjects[0] ?? null,
        taskTypes: taskTypesToUse,
        taskType: taskTypesToUse[0] ?? null,
        responseTypes: responseTypesToUse,
        responseType: responseTypesToUse[0] ?? null,
      };
    }

    try {
      await Promise.resolve(onSave(settings));
      onClose();
    } catch (err) {}
  };

  if (!shouldOpen) return null;

  // Detect duplicate subject titles to show "Name (Code)" only for duplicates
  const subjectTitleCounts = new Map();
  availableSubjects.forEach((opt) => {
    const title = opt != null && typeof opt === 'object'
      ? String(opt.title ?? opt.subjectName ?? opt.name ?? opt.label ?? '')
      : String(opt ?? '');
    if (title) subjectTitleCounts.set(title, (subjectTitleCounts.get(title) || 0) + 1);
  });

  const getSubjectLabel = (opt) => {
    if (opt != null && typeof opt === 'object') {
      const title = String(opt.title ?? opt.subjectName ?? opt.name ?? opt.label ?? '');
      const code = String(opt.subjectCode ?? opt.code ?? opt.value ?? '');
      if (title && subjectTitleCounts.get(title) > 1 && code && code !== title) {
        return `${title} (${code})`;
      }
      return title || code;
    }
    return String(opt ?? '');
  };

  const getSubjectValue = (opt) => {
    if (opt != null && typeof opt === 'object') {
      return opt.code ?? opt.subjectCode ?? opt.value ?? opt;
    }
    return opt;
  };

  return (
    <>
      <ModalOverlay onClick={onClose} />
      <ModalContainer>
        <ModalHeader>
          <HeaderTitle>
            <SettingsIconStyled src={settingsIcon} alt="Edit Customisations" />
            Edit Customisations
          </HeaderTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          <FieldGroup>
            <FieldLabel>Year</FieldLabel>
            <MultiSelectDropdown
              options={availableYears}
              selectedValues={selectedYears}
              onChange={setSelectedYears}
              placeholder="Select all that apply"
              getOptionLabel={(opt) => formatYearDisplay(opt)}
              alwaysWhite
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Subject</FieldLabel>
            <MultiSelectDropdown
              options={availableSubjects}
              selectedValues={selectedSubjects}
              onChange={setSelectedSubjects}
              placeholder="Select all that apply"
              getOptionLabel={getSubjectLabel}
              getOptionValue={getSubjectValue}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Task Type</FieldLabel>
            <MultiSelectDropdown
              options={availableTaskTypes}
              selectedValues={selectedTaskTypes}
              onChange={setSelectedTaskTypes}
              placeholder="Select all that apply"
              getOptionLabel={(opt) => (opt != null && typeof opt === 'object' ? String(opt.label ?? opt.title ?? opt.value ?? '') : String(opt ?? ''))}
              getOptionValue={(opt) => (opt != null && typeof opt === 'object' ? opt.value ?? opt : opt)}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Submission Type</FieldLabel>
            <MultiSelectDropdown
              options={availableResponseTypes}
              selectedValues={selectedResponseTypes}
              onChange={setSelectedResponseTypes}
              placeholder="Select all that apply"
              getOptionLabel={(opt) => (opt != null && typeof opt === 'object' ? String(opt.label ?? opt.title ?? opt.value ?? '') : String(opt ?? ''))}
              getOptionValue={(opt) => (opt != null && typeof opt === 'object' ? opt.value ?? opt : opt)}
            />
          </FieldGroup>
        </ModalBody>

        <ModalFooter>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </ModalFooter>
      </ModalContainer>
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 360px;
  max-width: 90vw;
  max-height: 90vh;
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #E8DDF7;
  border-bottom: 1px solid #D8C7E8;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const SettingsIconStyled = styled.img`
  width: 20px;
  height: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #6B7280;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #FFFFFF;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  background: #FFFFFF;
  border-top: 1px solid #E5E7EB;
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background-color: #7200E0;
  color: #FFFFFF;
  border: none;
  border-radius: 24px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;

  &:hover {
    background-color: #5B00B5;
  }

  &:active {
    background-color: #4A0091;
    transform: scale(0.98);
  }
`;

export default ItemSettingsModal;
